import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Stack, Divider, CircularProgress,
  Collapse, Tooltip, Snackbar, Alert, Slider,
} from '@mui/material';
import WifiIcon       from '@mui/icons-material/Wifi';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import axios from 'axios';
import { AuthContext }  from '../../components/signin/AuthContext';
import { useGrowSetup } from '../../GrowSetupContext';
import { useV4Resolve } from '../theme';

/* ─── Actor meta ─────────────────────────────────────── */
const ACTOR_META = {
  licht:   { icon: '☀️', color: 'var(--v4-gold)' },
  abluft:  { icon: '💨', color: 'var(--v4-teal)' },
  heizung: { icon: '🔥', color: '#ff5555' },
  umluft:  { icon: '🌀', color: '#a88fff' },
};
function getMeta(name) {
  const key = (name || '').toLowerCase();
  return ACTOR_META[key] || { icon: '⚡', color: 'var(--v4-cyan)' };
}

/* ─── Section label ──────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(var(--v4-cyan-rgb),0.6)', whiteSpace: 'nowrap' }}>
        {children}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: 'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

/* ─── Styled button pill ─────────────────────────────── */
function Pill({ label, active, color, onClick, disabled }) {
  return (
    <Box onClick={disabled ? undefined : onClick} sx={{
      px: 1.5, py: 0.45, borderRadius: '20px', cursor: disabled ? 'not-allowed' : 'pointer',
      border: `1px solid ${active ? `color-mix(in srgb, ${color} 67%, transparent)` : 'rgba(var(--v4-fg-rgb),0.08)'}`,
      background: active ? `color-mix(in srgb, ${color} 10%, transparent)` : 'transparent',
      color: active ? color : disabled ? 'rgba(var(--v4-fg-rgb),0.15)' : 'rgba(var(--v4-fg-rgb),0.35)',
      fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
      userSelect: 'none',
      transition: 'all 0.18s ease',
      '&:hover': disabled ? {} : { background: `color-mix(in srgb, ${color} 8%, transparent)`, color },
    }}>
      {label}
    </Box>
  );
}

/* ─── Single actor card ──────────────────────────────── */
function ActorCard({ actor, portOwner, onChannelChange, animDelay }) {
  const { token }      = useContext(AuthContext);
  const { grow_setup } = useGrowSetup();
  const meta = getMeta(actor.name);
  const color = useV4Resolve()(meta.color);

  const [watt,    setWatt]    = useState(actor.watt          ?? 0);
  const [mode,    setMode]    = useState(actor.rule_mode     ?? 'auto');
  const [channel, setChannel] = useState(String(actor.dimmer ?? '0'));
  const [ruleOn,  setRuleOn]  = useState(actor.rule_intervall_on  ?? 0);
  const [ruleOff, setRuleOff] = useState(actor.rule_intervall_off ?? 0);
  const [saving,  setSaving]  = useState(false);
  const [snack,   setSnack]   = useState({ open: false, msg: '', sev: 'success' });
  const [chanErr, setChanErr] = useState(null);

  const isIntervall = mode === 'intervall';
  const dirty =
    watt    !== (actor.watt          ?? 0) ||
    mode    !== (actor.rule_mode     ?? 'auto') ||
    channel !== String(actor.dimmer  ?? '0') ||
    ruleOn  !== (actor.rule_intervall_on  ?? 0) ||
    ruleOff !== (actor.rule_intervall_off ?? 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post('/save_actor_config', {
        grow_setup_id: grow_setup, rule_mode: mode, watt,
        dimmer: channel, actor_id: actor.actor_id,
        name: actor.name, rule_intervall_on: ruleOn, rule_intervall_off: ruleOff,
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } });
      setSnack({ open: true, msg: 'Gespeichert', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Fehler beim Speichern', sev: 'error' });
    } finally { setSaving(false); }
  };

  const handlePairing = async () => {
    try {
      await axios.post('/pairing_mode_on', { data: { grow_setup_id: grow_setup, actor_name: actor.name } }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setSnack({ open: true, msg: 'Pairing gestartet', sev: 'info' });
    } catch {
      setSnack({ open: true, msg: 'Fehler beim Pairing', sev: 'error' });
    }
  };

  const handleChannel = (val) => {
    const owner = portOwner[val];
    if (owner && owner !== (actor.actor_id ?? actor.id)) {
      setChanErr(`Kanal wird von "${owner}" verwendet`);
      return;
    }
    setChanErr(null);
    setChannel(val);
    onChannelChange(actor.actor_id ?? actor.id, parseInt(val, 10));
  };

  const CHANNELS = [
    { value: '1', label: 'Port 1' },
    { value: '2', label: 'Port 2' },
    { value: '3', label: 'Port 3' },
    { value: '0', label: 'Steckdose' },
  ];

  return (
    <>
      <Box sx={{
        background: 'rgba(var(--v4-surface-rgb),0.9)',
        border: `1px solid ${dirty ? `color-mix(in srgb, ${color} 33%, transparent)` : `color-mix(in srgb, ${color} 13%, transparent)`}`,
        borderRadius: '20px',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        animation: `v4FadeUp 0.55s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        boxShadow: dirty ? `0 0 30px color-mix(in srgb, ${color} 9%, transparent)` : 'none',
      }}>
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid rgba(var(--v4-cyan-rgb),0.07)` }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 42, height: 42, borderRadius: '12px',
              background: `color-mix(in srgb, ${color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 27%, transparent)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>
              {meta.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, color: 'var(--v4-text)', fontSize: '0.95rem', lineHeight: 1.1 }}>
                {actor.name}
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(var(--v4-muted-rgb),0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                ID: {actor.actor_id}
              </Typography>
            </Box>
            {dirty && (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
            )}
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack spacing={2.5}>
            {/* Channel */}
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(var(--v4-cyan-rgb),0.5)', mb: 1 }}>
                Kanal
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {CHANNELS.map(c => (
                  <Pill key={c.value} label={c.label} active={channel === c.value} color={color}
                    onClick={() => handleChannel(c.value)} />
                ))}
              </Stack>
              {chanErr && (
                <Typography sx={{ mt: 0.75, fontSize: '0.67rem', color: 'var(--v4-red)' }}>{chanErr}</Typography>
              )}
            </Box>

            {/* Mode */}
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(var(--v4-cyan-rgb),0.5)', mb: 1 }}>
                Modus
              </Typography>
              <Stack direction="row" gap={0.75}>
                <Pill label="Automatisch" active={mode === 'auto'}      color={color} onClick={() => setMode('auto')} />
                <Pill label="Intervall"   active={mode === 'intervall'} color={color} onClick={() => setMode('intervall')} />
              </Stack>
            </Box>

            {/* Watt */}
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(var(--v4-cyan-rgb),0.5)', mb: 1 }}>
                Nennleistung
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Slider
                    value={watt} min={0} max={4000} step={10}
                    size="small"
                    onChange={(_, v) => setWatt(Number(v))}
                    sx={{
                      color,
                      '& .MuiSlider-thumb': { width: 14, height: 14, '&:hover': { boxShadow: `0 0 0 8px color-mix(in srgb, ${color} 13%, transparent)` } },
                      '& .MuiSlider-rail': { opacity: 0.12 },
                    }}
                  />
                </Box>
                <Box sx={{
                  minWidth: 64, px: 1.5, py: 0.6, borderRadius: '10px',
                  background: `color-mix(in srgb, ${color} 6%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 19%, transparent)`,
                  textAlign: 'center',
                }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, color }}>
                    {watt} <span style={{ fontSize: '0.6rem', color: 'rgba(var(--v4-muted-rgb),0.4)' }}>W</span>
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Interval controls */}
            <Collapse in={isIntervall} unmountOnExit>
              <Box sx={{ p: 2, borderRadius: '14px', background: `color-mix(in srgb, ${color} 3%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 9%, transparent)` }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(var(--v4-cyan-rgb),0.5)', mb: 1.5 }}>
                  Intervall
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: '0.62rem', color: 'var(--v4-green)', fontWeight: 700, mb: 0.75 }}>⏵ EIN (min)</Typography>
                    <input
                      type="number" min={0} max={4300} step={1}
                      value={ruleOn}
                      onChange={e => setRuleOn(Number(e.target.value ?? 0))}
                      style={{
                        width: '100%', padding: '8px 12px',
                        borderRadius: 10, outline: 'none',
                        border: '1px solid rgba(var(--v4-green-rgb),0.25)',
                        background: 'rgba(var(--v4-green-rgb),0.06)',
                        color: 'var(--v4-text)', fontFamily: 'monospace', fontSize: '0.9rem',
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: '0.62rem', color: 'var(--v4-red)', fontWeight: 700, mb: 0.75 }}>⏸ AUS (min)</Typography>
                    <input
                      type="number" min={0} max={4300} step={1}
                      value={ruleOff}
                      onChange={e => setRuleOff(Number(e.target.value ?? 0))}
                      style={{
                        width: '100%', padding: '8px 12px',
                        borderRadius: 10, outline: 'none',
                        border: '1px solid rgba(var(--v4-red-rgb),0.25)',
                        background: 'rgba(var(--v4-red-rgb),0.06)',
                        color: 'var(--v4-text)', fontFamily: 'monospace', fontSize: '0.9rem',
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Stack>
        </Box>

        {/* Actions */}
        <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, borderTop: '1px solid rgba(var(--v4-cyan-rgb),0.07)' }}>
          <Stack direction="row" spacing={1.5}>
            <Tooltip title="Pairing-Modus starten">
              <Box onClick={handlePairing} sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                py: 1, borderRadius: '12px', cursor: 'pointer',
                background: 'rgba(var(--v4-cyan-rgb),0.06)', border: '1px solid rgba(var(--v4-cyan-rgb),0.18)',
                color: 'var(--v4-cyan)', fontSize: '0.75rem', fontWeight: 700,
                transition: 'all 0.18s ease',
                '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.12)', boxShadow: '0 4px 18px rgba(var(--v4-cyan-rgb),0.18)' },
              }}>
                <WifiIcon sx={{ fontSize: 16 }} /> Pairing
              </Box>
            </Tooltip>
            <Tooltip title={dirty ? 'Änderungen speichern' : 'Keine Änderungen'}>
              <Box onClick={dirty && !saving ? handleSave : undefined} sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                py: 1, borderRadius: '12px', cursor: dirty ? 'pointer' : 'not-allowed',
                background: dirty ? `color-mix(in srgb, ${color} 9%, transparent)` : 'rgba(var(--v4-fg-rgb),0.04)',
                border: `1px solid ${dirty ? `color-mix(in srgb, ${color} 31%, transparent)` : 'rgba(var(--v4-fg-rgb),0.08)'}`,
                color: dirty ? color : 'rgba(var(--v4-fg-rgb),0.2)',
                fontSize: '0.75rem', fontWeight: 700,
                transition: 'all 0.18s ease',
                '&:hover': dirty ? { background: `color-mix(in srgb, ${color} 16%, transparent)`, boxShadow: `0 4px 18px color-mix(in srgb, ${color} 13%, transparent)` } : {},
              }}>
                {saving
                  ? <CircularProgress size={14} sx={{ color }} />
                  : <SaveRoundedIcon sx={{ fontSize: 16 }} />
                }
                Speichern
              </Box>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} variant="filled" sx={{ borderRadius: '12px' }}
          onClose={() => setSnack(p => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ─── Page ───────────────────────────────────────────── */
export default function V4ActorConfigPage() {
  const { token }      = useContext(AuthContext);
  const { grow_setup } = useGrowSetup();
  const [actors,    setActors]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [portOwner, setPortOwner] = useState({});

  const buildPortOwner = (list) => {
    const map = {};
    list.forEach(a => { if (a.channel != null) map[a.channel] = a.name ?? a.actor ?? a.actor_id; });
    setPortOwner(map);
  };

  const load = () => {
    if (!grow_setup) return;
    setLoading(true);
    axios.post(`/get_actor_config/${grow_setup}`, {}, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setActors(list);
      buildPortOwner(list);
    }).catch(console.error)
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [grow_setup]);

  const handleChannelChange = (actor_id, channel) => {
    setPortOwner(prev => ({ ...prev, [channel]: actor_id }));
    load();
  };

  /* summary stats */
  const totalWatt  = actors.reduce((s, a) => s + (Number(a.watt) || 0), 0);
  const autoCount  = actors.filter(a => (a.rule_mode ?? 'auto') === 'auto').length;

  return (
    <Box>
      <style>{`@keyframes v4FadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Summary banner */}
      {!loading && actors.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, animation: 'v4FadeUp 0.4s both' }}>
          {[
            { label: 'Aktoren gesamt', value: actors.length, color: 'var(--v4-cyan)' },
            { label: 'Automatisch',    value: autoCount,     color: 'var(--v4-green)' },
            { label: 'Gesamtlast',     value: `${totalWatt} W`, color: 'var(--v4-orange)' },
          ].map(stat => (
            <Box key={stat.label} sx={{
              flex: '1 1 130px', minWidth: 120,
              background: 'rgba(var(--v4-surface-rgb),0.9)',
              border: `1px solid color-mix(in srgb, ${stat.color} 13%, transparent)`,
              borderRadius: '16px', px: 2.5, py: 1.75,
              boxShadow: `0 4px 20px color-mix(in srgb, ${stat.color} 6%, transparent)`,
            }}>
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(var(--v4-muted-rgb),0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {stat.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: stat.color, fontSize: '1.3rem', fontFamily: 'monospace' }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <SectionLabel>Aktor-Konfiguration</SectionLabel>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={32} sx={{ color: 'var(--v4-cyan)' }} />
        </Box>
      )}

      <Grid container spacing={2}>
        {actors.map((actor, i) => (
          <Grid item xs={12} sm={6} md={4} key={actor.id ?? actor.actor_id ?? i}>
            <ActorCard
              actor={actor}
              portOwner={portOwner}
              onChannelChange={handleChannelChange}
              animDelay={i * 60}
            />
          </Grid>
        ))}
      </Grid>

      {!loading && actors.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>⚡</Typography>
          <Typography sx={{ color: 'rgba(var(--v4-muted-rgb),0.3)', fontSize: '0.9rem' }}>
            Keine Aktoren konfiguriert.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
